import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ClockIcon from '@atlaskit/icon/core/clock';
import IconTile from '@atlaskit/icon/icon-tile';
import Modal, {
	CloseButton,
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import type {
	ScheduledChatDayOfWeek,
	ScheduledChatPayload,
} from '@atlassian/conversation-assistant-message-actions-api/scheduled-chat';

import { deduceNextRunAtIsoFromRRuleRaw } from './deduce-next-run-at-from-rrule';
import messages from './messages';
import { ScheduledChatFormFields } from './ScheduledChatFormFields';
import type { ScheduledChatEditorModalProps, ScheduledChatEditorSubmitContext } from './types';

const styles = cssMap({
	headerContentFullWidth: {
		width: '100%',
	},
	headerContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.200'),
		alignSelf: 'stretch',
		width: '100%',
	},
	headerMeta: {
		minWidth: '0px',
	},
	headerTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	contentSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.0'),
	},
	footer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingTop: token('space.200'),
		paddingBottom: token('space.300'),
	},
	footerActions: {
		flexShrink: 0,
	},
	pauseControl: {
		flexShrink: 0,
	},
});

export const ScheduledChatEditorModal = ({
	isOpen,
	mode,
	initialPayload,
	onClose,
	onSubmit,
	onDelete,
	onPauseChange,
	readonly = false,
	isSubmitting = false,
	initialPaused = false,
	rRule,
}: ScheduledChatEditorModalProps): React.JSX.Element => {
	const { formatMessage, formatDate } = useIntl();
	const [payload, setPayload] = useState<ScheduledChatPayload>(initialPayload);
	const [isPaused, setIsPaused] = useState(initialPaused);
	const wasModalOpenRef = useRef(false);

	useEffect(() => {
		const justOpened = isOpen && !wasModalOpenRef.current;
		wasModalOpenRef.current = isOpen;
		if (justOpened) {
			setPayload(initialPayload);
			setIsPaused(initialPaused);
		}
	}, [isOpen, initialPayload, initialPaused]);

	const handleSubmit = useCallback(() => {
		const context: ScheduledChatEditorSubmitContext = { isPaused };
		onSubmit(payload, context);
	}, [onSubmit, payload, isPaused]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	const updatePayload = useCallback((updates: Partial<ScheduledChatPayload>) => {
		setPayload((prev) => ({ ...prev, ...updates }));
	}, []);

	const handleNameChange = useCallback((name: string) => updatePayload({ name }), [updatePayload]);

	const handlePromptChange = useCallback(
		(prompt: string) => updatePayload({ prompt }),
		[updatePayload],
	);

	const handleFrequencyChange = useCallback(
		(frequency: ScheduledChatPayload['frequency']) => updatePayload({ frequency }),
		[updatePayload],
	);

	const handleDaysOfWeekChange = useCallback(
		(daysOfWeek: ScheduledChatDayOfWeek[]) => updatePayload({ daysOfWeek }),
		[updatePayload],
	);

	const handleTimeChange = useCallback((time: string) => updatePayload({ time }), [updatePayload]);

	const isCreateMode = mode === 'create';
	const isRenameMode = mode === 'rename';
	const isFormValid = Boolean(payload.name.trim() && payload.prompt.trim());

	const modalTitle = isCreateMode
		? formatMessage(messages.createModalTitle)
		: isRenameMode
			? formatMessage(messages.renameModalTitle)
			: formatMessage(messages.editModalTitle);

	const submitButtonText = isCreateMode
		? formatMessage(messages.createButton)
		: formatMessage(messages.saveButton);

	const nextRunSubtitle = useMemo(() => {
		if (isCreateMode) {
			return undefined;
		}
		const nextRunIso = deduceNextRunAtIsoFromRRuleRaw(rRule);
		if (!nextRunIso) {
			return undefined;
		}
		const dateLabel = formatDate(nextRunIso, { day: 'numeric', month: 'short' });
		const timeLabel = formatDate(nextRunIso, { hour: 'numeric', minute: '2-digit' });
		const dateAndTime = formatMessage(messages.dateAtTime, {
			date: dateLabel,
			time: timeLabel,
		});
		return formatMessage(messages.editModalNextRunAt, {
			nextRunLabel: dateAndTime,
		});
	}, [isCreateMode, rRule, formatDate, formatMessage]);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={handleClose}
					width="large"
					testId="scheduled-chat-editor-modal"
					shouldCloseOnEscapePress
					shouldCloseOnOverlayClick
				>
					<ModalHeader>
						<Box xcss={styles.headerContentFullWidth}>
							<Inline
								xcss={styles.headerContent}
								spread="space-between"
								alignBlock="start"
								shouldWrap={false}
							>
								<ModalTitle>
									<Inline alignBlock="center" space="space.150">
										<IconTile icon={ClockIcon} label="" size="medium" appearance="gray" />
										<Stack space="space.0" xcss={styles.headerMeta}>
											<Box as="span" xcss={styles.headerTitle}>
												<Heading as="span" size="xsmall">
													{payload.name || modalTitle}
												</Heading>
											</Box>
											{!!nextRunSubtitle && (
												<Text size="small" color="color.text.subtlest">
													{nextRunSubtitle}
												</Text>
											)}
										</Stack>
									</Inline>
								</ModalTitle>
								<CloseButton onClick={handleClose} />
							</Inline>
						</Box>
					</ModalHeader>
					<ModalBody>
						<Box xcss={styles.contentSection}>
							<ScheduledChatFormFields
								formatMessage={formatMessage}
								formatDate={formatDate}
								readonly={readonly}
								isActionLoading={isSubmitting}
								name={payload.name}
								prompt={payload.prompt}
								frequency={payload.frequency}
								daysOfWeek={payload.daysOfWeek}
								time={payload.time}
								onNameChange={handleNameChange}
								onPromptChange={handlePromptChange}
								onFrequencyChange={handleFrequencyChange}
								onDaysOfWeekChange={handleDaysOfWeekChange}
								onTimeChange={handleTimeChange}
							/>
							<Box xcss={styles.footer}>
								<Inline xcss={styles.pauseControl} alignBlock="center" space="space.100">
									<Toggle
										isChecked={isPaused}
										onChange={(event) => {
											const nextChecked = event.currentTarget.checked;
											setIsPaused(nextChecked);
											onPauseChange?.(payload, nextChecked);
										}}
										label={formatMessage(messages.editModalPause)}
									/>
									<Text size="medium">{formatMessage(messages.editModalPause)}</Text>
								</Inline>
								<Inline xcss={styles.footerActions} space="space.100">
									<Button appearance="subtle" onClick={handleClose} isDisabled={isSubmitting}>
										{formatMessage(messages.cancelButton)}
									</Button>
									{(mode === 'edit' || mode === 'rename') && onDelete && (
										<Button appearance="default" onClick={() => onDelete(payload)}>
											{formatMessage(messages.editModalDelete)}
										</Button>
									)}
									<Button
										appearance="primary"
										onClick={handleSubmit}
										isLoading={isSubmitting}
										isDisabled={!isFormValid || readonly}
										testId="scheduled-chat-submit"
									>
										{submitButtonText}
									</Button>
								</Inline>
							</Box>
						</Box>
					</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};
